// @ts-ignore
import { ScatterplotLayer } from "@deck.gl/layers";

const ZOOM_REFERENCE = 14;
const ZOOM_SCALE_FACTOR = 1.15;

const getScaledRadius = (baseRadius: number, mapZoom?: number) => {
    if (!mapZoom) return baseRadius;
    return Math.max(2, baseRadius * Math.pow(ZOOM_SCALE_FACTOR, mapZoom - ZOOM_REFERENCE));
};

export const PoILayer = ({
    showPoi,
    poi,
    onHoverPoi,
    handleClick,
    focusedItem,
    isGeoFocused,
    brand,
    markerType,
    markerColor,
    mapZoom,
}: any) => {
    if (!showPoi || !poi?.length) return [];

    const type = markerType ?? 'poi';
    const [r, g, b] = markerColor ?? [0, 0, 0];

    const visualLayer = new ScatterplotLayer({
        id: `${type}-layer-visual`,
        data: poi,
        getPosition: (d: any) => [d.longitude, d.latitude],
        radiusUnits: "pixels",
        getRadius: (d: any) => getScaledRadius(d.isSelected ? 7.5 : 5, mapZoom),
        getFillColor: (d: any) => {
            const isClicked = focusedItem?.type === type &&
                focusedItem?.data?.latitude === d.latitude &&
                focusedItem?.data?.longitude === d.longitude;
            const isHovered = brand?.latitude === d.latitude &&
                brand?.longitude === d.longitude &&
                (!markerType || brand?._markerType === markerType);

            if (isClicked || isHovered) {
                return markerColor ? [255, 255, 255, 255] : (d.isSelected ? [167, 188, 255, 255] : [255, 255, 255, 255]);
            }

            if (markerColor) {
                if (d.filtered_out) return [175, 175, 175, 255];
                return d.isSelected
                    ? [Math.round(r * 0.5), Math.round(g * 0.5), Math.round(b * 0.5), 255]
                    : [Math.round(r * 0.35 + 166), Math.round(g * 0.35 + 166), Math.round(b * 0.35 + 166), 255];
            }

            return d.filtered_out ? [175, 175, 175, 255] : d.isSelected ? [0, 48, 199, 255] : [174, 194, 255, 255];
        },
        stroked: true,
        lineWidthUnits: "pixels",
        getLineColor: (d: any) => {
            const isClicked = focusedItem?.type === type &&
                focusedItem?.data?.latitude === d.latitude &&
                focusedItem?.data?.longitude === d.longitude;
            const isHovered = brand?.latitude === d.latitude &&
                brand?.longitude === d.longitude &&
                (!markerType || brand?._markerType === markerType);

            if (markerColor) {
                if (isClicked) {
                    return d.isSelected
                        ? [Math.round(r * 0.5), Math.round(g * 0.5), Math.round(b * 0.5), 255]
                        : [Math.round(r * 0.3), Math.round(g * 0.3), Math.round(b * 0.3), 255];
                }
                if (isHovered) {
                    return d.isSelected
                        ? [Math.round(r * 0.6 + 102), Math.round(g * 0.6 + 102), Math.round(b * 0.6 + 102), 255]
                        : [r, g, b, 255];
                }
                if (d.filtered_out) return [69, 69, 69, 255];
                return d.isSelected
                    ? [Math.round(r * 0.6 + 102), Math.round(g * 0.6 + 102), Math.round(b * 0.6 + 102), 255]
                    : [Math.round(r * 0.3), Math.round(g * 0.3), Math.round(b * 0.3), 255];
            }

            if (isClicked) {
                return d.isSelected ? [0, 48, 199, 255] : [7, 32, 106, 255];
            }
            if (isHovered) {
                return d.isSelected ? [227, 234, 255, 255] : [141, 169, 255, 255];
            }
            return d.filtered_out ? [69, 69, 69, 255] : d.isSelected ? [184, 201, 255, 255] : [7, 32, 106, 255];
        },
        getLineWidth: () => 1,
        updateTriggers: {
            getFillColor: [focusedItem, brand],
            getLineColor: [focusedItem, brand],
            getRadius: [focusedItem, mapZoom],
            getPosition: [poi.length]
        },
        pickable: false,
    });

    const interactionLayer = new ScatterplotLayer({
        id: `${type}-layer-interaction`,
        data: poi,
        getPosition: (d: any) => [d.longitude, d.latitude],
        radiusUnits: "pixels",
        getRadius: () => getScaledRadius(15, mapZoom),
        getFillColor: () => [0, 0, 0, 0],
        stroked: false,
        pickable: true,
        onHover: (info: any) => {
            const { object, pixel } = info;
            if (object)
                onHoverPoi && !focusedItem.type && !isGeoFocused && onHoverPoi(type, object ?? null, pixel);
            else onHoverPoi && !isGeoFocused && onHoverPoi(type, null);
        },
        onClick: (info: any) => {
            const { object, pixel } = info;
            handleClick && object && handleClick(type, object ?? null, pixel);
        },
        updateTriggers: {
            getPosition: [poi.length]
        },
    });

    return [visualLayer, interactionLayer];
};
